import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, Save } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg } from "../../lib/api";
import { Field } from "../../components/site/ProfileFields";
import { AdminH } from "./AdminLayout";
import { FormField } from "./FormField";

const LABELS = { site: "Site settings & logo", images: "Site photos", home: "Home page", about: "About us", contacts: "Contacts", event_defaults: "Standard texts for clinic sections (all classes)", gdpr: "GDPR popup", privacy: "Privacy policy" };

const aiTranslate = async (collection, id) => {
  const t = toast.loading("Translating with AI…");
  try {
    await api.post(`/admin/translate/${collection}/${id}`);
    toast.success("IT/ES generated. Review them in Translations.", { id: t });
  } catch (e) {
    toast.error(errMsg(e), { id: t });
  }
};

const PageEditor = ({ doc, reload }) => {
  const [fields, setFields] = useState(doc.fields || {});
  useEffect(() => { setFields(doc.fields || {}); }, [doc]);
  const save = async () => {
    try {
      await api.put(`/admin/content/${doc.key}`, { fields, translations: doc.translations || {} });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid={`content-editor-${doc.key}`}>
      {Object.entries(fields).map(([k, v]) => doc.key === "images" ? (
        <FormField key={k} field={{ key: k, label: { hero: "Home slider – image 1", hero_2: "Home slider – image 2", hero_3: "Home slider – image 3", hero_4: "Home slider – image 4" }[k] || k.replace(/_/g, " "), type: "image", full: true }} value={v} onChange={(x) => setFields({ ...fields, [k]: x })} />
      ) : (
        <Field key={k} label={k.replace(/_/g, " ")}>
          {String(v).length > 80 ? <Textarea rows={4} value={v} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} data-testid={`content-${doc.key}-${k}`} />
            : <Input value={v} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} data-testid={`content-${doc.key}-${k}`} />}
        </Field>
      ))}
      <div className="flex gap-3">
        <button onClick={save} className="btn-buoy !py-2" data-testid={`content-save-${doc.key}`}><Save size={16} />Save</button>
        {!["site", "images"].includes(doc.key) && <button onClick={() => aiTranslate("content", doc.key)} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold" data-testid={`content-translate-${doc.key}`}><Languages size={16} />AI translate</button>}
      </div>
    </div>
  );
};

export default function AdminContent() {
  const { data, reload } = useFetch("/admin/content");
  if (!data) return null;
  const order = ["site", "images", "home", "about", "contacts", "event_defaults", "gdpr", "privacy"];
  const docs = order.map((k) => data.find((d) => d.key === k)).filter(Boolean);
  return (
    <div data-testid="admin-content">
      <AdminH title="Pages & defaults" sub="Public area content in English; translate with AI and review in Translations. Site photos: upload or paste a URL to replace any image (events, classes, locations, charter and news photos are editable in their own sections)." />
      <Tabs defaultValue="home">
        <TabsList className="flex-wrap">{docs.map((d) => <TabsTrigger key={d.key} value={d.key} data-testid={`content-tab-${d.key}`}>{LABELS[d.key]}</TabsTrigger>)}</TabsList>
        {docs.map((d) => <TabsContent key={d.key} value={d.key} className="mt-6"><PageEditor doc={d} reload={reload} /></TabsContent>)}
      </Tabs>
    </div>
  );
}

const TranslationItem = ({ item, onSaved }) => {
  const [tr, setTr] = useState({ it: item.translations.it || {}, es: item.translations.es || {} });
  useEffect(() => { setTr({ it: item.translations.it || {}, es: item.translations.es || {} }); }, [item]);
  const status = (l) => item.translations[l]?._status || "missing";
  const save = async () => {
    try {
      await api.put(`/admin/translations/${item.collection}/${item.id}`, tr);
      toast.success("Translations saved as reviewed");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const translate = async () => { await aiTranslate(item.collection, item.id); onSaved(); };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`translation-item-${item.collection}-${item.id}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-lg font-bold text-[#0A2540]">{item.label} <span className="text-xs font-normal uppercase text-slate-400">{item.collection}</span></p>
        <div className="flex items-center gap-2">
          {["it", "es"].map((l) => <span key={l} className={`rounded-full px-2 py-0.5 text-xs font-bold ${status(l) === "reviewed" ? "bg-emerald-100 text-emerald-700" : status(l) === "ai" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{l.toUpperCase()}: {status(l)}</span>)}
          <button onClick={translate} className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold" data-testid={`translation-ai-${item.id}`}><Languages size={14} />AI translate</button>
          <button onClick={save} className="btn-buoy !px-4 !py-1.5 !text-xs" data-testid={`translation-save-${item.id}`}>Save reviewed</button>
        </div>
      </div>
      <div className="space-y-4">
        {Object.entries(item.source).map(([k, src]) => (
          <div key={k} className="grid gap-3 lg:grid-cols-3">
            <div><p className="mb-1 text-[10px] font-bold uppercase text-slate-400">EN · {k}</p><p className="whitespace-pre-line rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{src}</p></div>
            {["it", "es"].map((l) => (
              <div key={l}><p className="mb-1 text-[10px] font-bold uppercase text-slate-400">{l}</p>
                <Textarea rows={3} className="text-xs" value={tr[l][k] || ""} onChange={(e) => setTr({ ...tr, [l]: { ...tr[l], [k]: e.target.value } })} data-testid={`translation-${item.id}-${l}-${k}`} /></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export function AdminTranslations() {
  const { data, reload } = useFetch("/admin/translations");
  const [col, setCol] = useState("events");
  const cols = [...new Set((data || []).map((d) => d.collection))];
  return (
    <div data-testid="admin-translations">
      <AdminH title="Translations review" sub="Automatic AI translation (EN → IT/ES), editable and approvable by the administrator" />
      <div className="mb-6 flex flex-wrap gap-2">
        {cols.map((c) => <button key={c} onClick={() => setCol(c)} data-testid={`translations-filter-${c}`} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${col === c ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white"}`}>{c}</button>)}
      </div>
      <div className="space-y-4">{(data || []).filter((d) => d.collection === col).map((it) => <TranslationItem key={it.id} item={it} onSaved={reload} />)}</div>
    </div>
  );
}
